import { EventIdentifier, FeatureTeamEvent, TrackingEventType, useAnalytics, useIntl } from '@capsicum/common';
import { TextInput } from '@capsicum/common-web';
import { type RegisterSchemaType, type RegisterStepProps, useEmailAddressStepValidation } from '@capsicum/socios-core';
import Link from 'next/link';
import { type ReactNode, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { AgeConfirmation } from '../AgeConfirmation';
import { MarketingConcent } from '../MarketingConcent';
import { Checkboxes } from '../Register.styled';
import { StepperPage } from '../StepperPage';
import { TermsAndConditions } from '../TermsAndConditions';

export const EmailAddressStep = ({ isTermsAndConditionsVisible, onValidityChange }: RegisterStepProps) => {
	const { $t } = useIntl();
	const analytics = useAnalytics();
	const { control, formState } = useFormContext<RegisterSchemaType>();

	useEmailAddressStepValidation({ isTermsAndConditionsVisible, onValidityChange });

	const debouncedErrorEvent = useDebouncedCallback(
		(errorMessage = '') => {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
				extra_parameter: errorMessage,
				feature_team: FeatureTeamEvent.ORFT,
				page: 'email_entry_page',
				text: 'email_error_triggered'
			});
		},
		2500,
		{ leading: true, trailing: false }
	);

	const error = useMemo(() => {
		const error = formState.errors.email;

		if (!error) return undefined;

		debouncedErrorEvent(error.message);

		return error.message !== $t('auth.emailTaken')
			? error.message
			: $t('auth.emailTakenWithLoginLink', {
					link: (chunks: ReactNode) => <S.LinkVariant href="/login">{chunks}</S.LinkVariant>
				});
	}, [$t, formState.errors.email, debouncedErrorEvent]);

	useEffect(
		() =>
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
				feature_team: FeatureTeamEvent.OFT,
				page: 'email_entry_page'
			}),
		[analytics]
	);

	return (
		<StepperPage title={$t('auth.registration.emailStep.title')}>
			<div>
				<Controller
					control={control}
					name="email"
					render={({ field, fieldState: { error } }) => {
						return (
							<TextInput
								{...field}
								autoCapitalize="none"
								autoComplete="email"
								error={
									error
										? {
												message: '',
												type: error.type
											}
										: undefined
								}
								label={$t('auth.registrationEmail')}
								name="email"
								testID="registration-form-email-input"
								type="email"
							/>
						);
					}}
				/>

				{error && <S.SupportingInputInfo>{error}</S.SupportingInputInfo>}
			</div>

			<Checkboxes>
				<AgeConfirmation />
				<MarketingConcent />
				{isTermsAndConditionsVisible && <TermsAndConditions key="terms-and-conditions" />}
			</Checkboxes>
		</StepperPage>
	);
};

namespace S {
	export const SupportingInputInfo = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.borders.error.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const LinkVariant = styled(Link)`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}
